import { Component, Element, Event, EventEmitter, h, Listen, Prop, State, Watch } from '@stencil/core';

import { KEYBOARD } from '../../../config/constants';
import { getCSSVariable, getVariableGeneric, hasSlotContent } from '../../../services/utils';

/**
 * @part base - The `<div>` or `<label>` HTML element used as the base container.
 * @part checkbox - The `<input>` HTML element used to simulate the checkbox.
 * @part container-icon - The `<div>` HTML element used as the container for the icon.
 * @part icon - The `<pk-icon>` HTML element used to display the icon.
 * @part prefix - The `<div>` HTML element used as the prefix container.
 * @part body-container - The `<div>` HTML element used as the body container.
 * @part primary - The `<div>` HTML element used as the primary text container.
 * @part secondary - The `<div>` HTML element used as the secondary text container.
 * @part suffix - The `<div>` HTML element used as the suffix container.
 */
@Component({
  tag: 'pk-identifier',
  styleUrl: './scss/pk-identifier.scss',
  shadow: true,
})
export class PkBasicIdentifier {
  // Own Properties
  // ====================
  private cssVarPrefix = '--identifier';
  private prefixElem: HTMLDivElement;
  private suffixElem: HTMLDivElement;

  // Reference to host HTML element
  // ===================================

  @Element() el!: HTMLPkIdentifierElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  @State() hasPrefixContent: boolean = true;
  @State() hasSuffixContent: boolean = false;

  // Public Property API
  // ========================

  /** Type (horizontal or vertical) **/
  @Prop({ reflect: true }) type: 'horizontal' | 'vertical' = 'horizontal';

  /** Order (primary and secondary vertical order) **/
  @Prop({ reflect: true }) order: 'normal' | 'inverted' = 'normal';

  /** Horizontal elements alignment **/
  @Prop({ reflect: true, mutable: true }) alignment?: 'left' | 'right' | 'center';

  /** Specify primary size font */
  @Prop({ reflect: true }) primarySize: string | number;

  /** Specify secondary size font */
  @Prop({ reflect: true }) secondarySize: string | number;

  /** Specify a token as the vertical padding*/
  @Prop({ reflect: true }) padY: string | number;

  /** Is identifier selected */
  @Prop({ reflect: true }) selected: boolean = false;

  /** If true, the identifier has the option to be selected */
  @Prop({ reflect: true }) enableSelection: boolean = false;

  /** Set the background color */
  @Prop({ reflect: true }) bgColor: string;

  /** Activate border radius */
  @Prop({ reflect: true }) rounded: boolean = false;

  /** Custom border radius (active only if 'rounded' or 'selected') */
  @Prop({ reflect: true }) borderRadius: string | number;

  /** If true, the primary text will be wrapped in two or more rows (it won't be cut with the three dots `...`) */
  @Prop({ reflect: true }) primaryMultiRow: boolean = false;

  // Prop lifecycle events
  // =======================
  @Watch('alignment')
  handleAlignmentChange() {
    if (this.alignment) return;

    if (this.type === 'vertical') {
      this.alignment = 'center';
      return;
    }

    this.alignment = this.hasSuffixContent && !this.hasPrefixContent ? 'right' : 'left';
  }

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  /** On identifier selected event emitter
   * @deprecated use pkChange event instead
   */
  @Event({ eventName: 'pkIdentifierSelected' }) pkIdentifierSelected: EventEmitter<{
    element: HTMLElement;
    selected: boolean;
  }>;

  /** On identifier selected stage change event emitter */
  @Event({ eventName: 'pkChange' }) pkChange: EventEmitter<{
    element: HTMLElement;
    selected: boolean;
  }>;

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================
  componentWillLoad() {
    this.handleAlignmentChange();
  }

  componentDidLoad() {
    this.handlePrefixSlotChange();
    this.handleSuffixSlotChange();
  }

  // Listeners
  // ==============

  /**
   * If the event target is this element and selection is enabled, treat Enter as a click.
   */
  @Listen('keydown', { target: 'document' })
  onKeyDown(ev: KeyboardEvent): void {
    const { target, key } = ev;
    const { Enter } = KEYBOARD;

    // If the event isn't Enter, or if selection isn't enabled, do nothing.
    if (key !== Enter || !this.enableSelection) return;
    // If the event target isn't this element, do nothing.
    if (target !== this.el) return;

    this.onSelect(!this.selected);
  }

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  private onChange = (ev: Event) => {
    const checked = (ev.target as HTMLInputElement).checked;
    this.onSelect(checked);
  };

  private onSelect = (selected: boolean) => {
    this.selected = selected;

    this.pkChange.emit({
      element: this.el,
      selected: this.selected,
    });

    // Deprecated event
    this.pkIdentifierSelected.emit({
      element: this.el,
      selected: this.selected,
    });
  };

  private handlePrefixSlotChange = () => {
    this.hasPrefixContent = hasSlotContent(this.prefixElem);
    this.handleAlignmentChange();
  };

  private handleSuffixSlotChange = () => {
    this.hasSuffixContent = hasSlotContent(this.suffixElem);
    this.handleAlignmentChange();
  };

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    const styles = {
      ...(this.padY && { [`${this.cssVarPrefix}--pad-y`]: getVariableGeneric(this.padY, 'px') }),
      ...(this.primarySize && { [`${this.cssVarPrefix}-primary--size`]: getVariableGeneric(this.primarySize, 'px') }),
      ...(this.secondarySize && {
        [`${this.cssVarPrefix}-secondary--size`]: getVariableGeneric(this.secondarySize, 'px'),
      }),
      ...(this.borderRadius && {
        [`${this.cssVarPrefix}--border-radius`]: getVariableGeneric(this.borderRadius, 'px'),
      }),
      ...(this.bgColor && {
        [`${this.cssVarPrefix}--bg-color`]: getCSSVariable(this.bgColor),
      }),
    };

    const TagElem = this.enableSelection ? 'label' : 'div';

    return (
      <TagElem
        style={{ ...styles }}
        class={{
          [`pk-identifier ${this.type}`]: true,
          selected: this.selected,
          selectable: this.enableSelection,
          'has-prefix': this.type !== 'vertical' && this.hasPrefixContent,
          'has-suffix': this.type !== 'vertical' && this.hasSuffixContent,
          inverted: this.order === 'inverted',
          [`alignment--${this.alignment}`]: !!this.alignment,
          rounded: this.rounded,
        }}
        part="base"
      >
        {this.enableSelection && (
          <input
            type="checkbox"
            onChange={this.onChange}
            checked={this.selected}
            class="absolute opacity-0"
            part="checkbox"
          />
        )}
        {this.enableSelection && this.selected && (
          <div class="absolute right-1.5 top-1.5" part="container-icon">
            <pk-icon name="actions-check" color="support-01" size="16" part="icon"></pk-icon>
          </div>
        )}
        <div class="prefix" part="prefix" ref={(el: HTMLDivElement) => (this.prefixElem = el)}>
          <slot name="prefix" onSlotchange={this.handlePrefixSlotChange} />
        </div>
        <div class="content flex min-w-0 max-w-full flex-col justify-center" part="body-container">
          <div class={{ primary: true, 'multi-row': this.primaryMultiRow }} part="primary">
            <slot name="primary" />
          </div>
          <div class="secondary" part="secondary">
            <slot name="secondary" />
          </div>
        </div>
        <div class="suffix" part="suffix" ref={(el: HTMLDivElement) => (this.suffixElem = el)}>
          <slot name="suffix" onSlotchange={this.handleSuffixSlotChange} />
        </div>
      </TagElem>
    );
  }
}
